/**
 * Helpers for remembering where the user navigated into a CMS detail page from,
 * so a "Back" button can return there with an explicit `router.push`.
 *
 * Why not `router.back()`? A global `PreventBrowserNavigation` trap (mounted in
 * src/app/layout.tsx) blocks every browser back/forward by bouncing `popstate`
 * forward, so `router.back()` is a no-op app-wide. CMS sections such as
 * "Enhance your trip" and "Useful information" appear on both `/` and `/search`,
 * so there is no single static parent — we record the origin on card click and
 * push it back on "Back".
 */

const STORAGE_PREFIX = "backPath:";

/** Records the current path (including query) as the origin for the given key. */
export function rememberBackPath(key: string): void {
  if (typeof window === "undefined") return;
  try {
    const current = window.location.pathname + window.location.search;
    window.sessionStorage.setItem(STORAGE_PREFIX + key, current);
  } catch {
    // sessionStorage can throw in private mode / when storage is disabled.
  }
}

/**
 * Returns and clears the remembered origin path for the given key.
 * Falls back to `fallback` when nothing was recorded (e.g. deep links).
 */
export function consumeBackPath(key: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    window.sessionStorage.removeItem(STORAGE_PREFIX + key);
    return stored || fallback;
  } catch {
    return fallback;
  }
}

/** Storage keys for the CMS detail pages that use remembered back navigation. */
export const BACK_PATH_KEYS = {
  enhancedTrips: "enhancedTrips",
  usefulInfo: "usefulInfo",
} as const;
